import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const buyingTimingFactors = [
    {
        id: 'month-end',
        title: 'End of Month and Quarter Timing',
        text: 'Dealer targets are set on a monthly and quarterly basis. When sales teams are close to hitting a volume goal, they may be more flexible on pricing to close a deal before the deadline.'
    },
    {
        id: 'year-end',
        title: 'Why Year-End Can Be Stronger',
        text: 'Late in the year, dealers face pressure to clear out current-year inventory for incoming models and hit annual targets. This often creates the strongest negotiating window of the entire calendar year.'
    },
    {
        id: 'model-year',
        title: 'New Model Year Changeovers',
        text: 'When next year’s models arrive, the "last year’s" models still on the lot lose pricing power. If the updates are minor, buying the outgoing model version can lead to substantial savings.'
    },
    {
        id: 'seasonal',
        title: 'Seasonal Timing That Helps',
        text: 'Demand for certain vehicles shifts with the weather. Convertibles are often cheaper in winter, while AWD SUVs may have less negotiating room right before the first snowstorm.'
    }
];

const faqs = [
    {
        question: 'Is the end of the month always the best time to buy?',
        answer: 'Not always, but it increases your leverage if the dealership is just a few units shy of a monthly sales bonus. It’s a tactical window worth targeting.'
    },
    {
        question: 'Is year-end better than every other time?',
        answer: 'Year-end is generally excellent for outgoing inventory, but localized inventory levels and manufacturer incentives can sometimes create better deals in other months.'
    },
    {
        question: 'Should I wait for a holiday sale?',
        answer: 'Holiday sales provide a good framework for incentives, but you should always compare the specific "sale" price against market data to ensure it’s a real discount.'
    },
    {
        question: 'What matters most if I need a car soon?',
        answer: 'If you can’t wait for a specific date, focus on your preparation: have your financing pre-approved, compare multiple dealers, and know your walk-away price.'
    }
];

const Blog17 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 17;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            When Is the Best Time to Buy a Car? (2026 Guide)
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            A lot of people start car shopping when they need a car right away, losing their biggest advantage: timing.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 6 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/17_1.jpeg"
                            alt="Car dealership showroom and buying decision"
                            title="When Is the Best Time to Buy a Car?"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: car_showroom_deal.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#month-end" className="text-gray-900 no-underline hover:underline">Month-end timing</a></li>
                            <li><a href="#year-end" className="text-gray-900 no-underline hover:underline">Year-end benefits</a></li>
                            <li><a href="#model-year" className="text-gray-900 no-underline hover:underline">Model year changeovers</a></li>
                            <li><a href="#seasonal" className="text-gray-900 no-underline hover:underline">Seasonal demand</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            The calendar matters because dealer targets matter. End of the month, end of the quarter, and late in the calendar year often create better negotiating conditions, especially on outgoing inventory.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Buying out of peak seasonal demand can improve your leverage. The best time to buy is when timing, inventory, and your own readiness line up together.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>When should you buy?</strong><br />
                            Generally, the best time to buy is late in the month (last 2-3 days) or late in the year (December). This is when dealers are most motivated to hit their sales targets.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/17_2.jpeg"
                            alt="Buyer discussing timing and pricing at a dealership"
                            title="Timing your car purchase"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: buyer_pricing_discussion.png
                        </figcaption>
                    </figure>

                    {buyingTimingFactors.map((item) => (
                        <section key={item.id} id={item.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {item.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{item.text}</p>
                        </section>
                    ))}

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Timing the market is easier with data. CarFido Assistant helps you track the best opportunities and store your research in one place.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Track timing opportunities based on dealer cycles and inventory trends</li>
                                <li>Compare total ownership cost of different models before you commit</li>
                                <li>Store your purchase research, quotes, and deal comparisons</li>
                                <li>Navigate complex negotiations with data-backed alternative options</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            There is no magic date for everyone, but there are better windows than others. The strongest buyers are those who know their budget and wait for dealer pressure to meet buyer preparation.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Timing helps, but being ready with pre-approved financing and a clear price limit is what turns a good date into real savings.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#month-end" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Month-end</a></li>
                            <li><a href="#year-end" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Year-end</a></li>
                            <li><a href="#model-year" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Changeovers</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog17;
